import { ClassPerformanceReport } from "../../classPerformance/ClassPerformanceReport.component";
import CustomReportService from "../../../../services/reports/CustomReport.service";
import { MoneyDataItem } from "../../../../interfaces/reports/BaseReport.interface";
import { useState } from "react";

interface TootsiesClassPerformanceProps {
  clientId: string | number;
  epochMonth: number | null;
}

const brandHierarchy: Record<string, string[]> = {
    "By Brand": ["brand"],
    "Location by Brand": ["locationCode", "brand"],
    "Location by Class by Brand": ["locationCode", "classCode", "brand"],
    "Class by Brand": ["classCode", "brand"],
    "Class by Brand by Location": ["classCode", "brand", "locationCode"],
    "Category by Class by Brand": ["category", "classCode", "brand"],
    "Category by Brand": ["category", "brand"],
    "Category by Location by Brand": ["category", "locationCode", "brand"],
    "Location by Category by Brand": ["locationCode", "category", "brand"],
    "Category by Brand by Location": ["category", "brand", "locationCode"],
    "By Location": ["locationCode"],
    "Location By Class": ["locationCode", "classCode"],
    "Location By Category": ["locationCode", "category"],
    "Location by Category by Class": ["locationCode", "category", "classCode"],
    "By Class": ["classCode"],
    "Class by Location": ["classCode", "locationCode"],
    "By Category": ["category"],
    "Category by Location": ["category", "locationCode"],
    "Category by Class": ["category", "classCode"],
    "Category by Class by Location": ["category", "classCode", "locationCode"],
};

const TootsiesClassPerformanceReport = ({ clientId, epochMonth }: TootsiesClassPerformanceProps) => {
  const [showOnlyActuals, setShowOnlyActuals] = useState<boolean>(true);

  // Parse function override to add brand actuals filtering
  const parseDataFn = (raw: any[]) => {
    if (!Array.isArray(raw) || raw.length === 0) return [];
    const filtered = raw.filter((r) => {
      if (!showOnlyActuals) return true;
      const sales = Number(r?.sales ?? r?.salesLm ?? 0);
      const markdowns = Number(r?.markdowns ?? r?.markdownsL3 ?? 0);
      const inventory = Number(r?.inventory ?? r?.invCurrent ?? r?.invAvg ?? r?.inventoryBomRm ?? 0);
      return (sales > 0) || (markdowns > 0) || (inventory > 0)
    });
    return filtered.map((r) => ({
      locationCode: r.locationCode,
      locationName: r.locationName,
      classCode: r.classCode,
      className: r.className,
      parentClientClassCode: r.parentClientClassCode ?? null,
      category: r.category ?? 'N/A',
      categorySortOrder: r.categorySortOrder ?? null,
      brand: r.brand ?? 'Non-Branded',
      // Main columns used in ClassPerformance
      sales: r.sales != null ? new MoneyDataItem(r.sales) : null,
      inventory: r.inventory != null ? new MoneyDataItem(r.inventory) : null,
      inflowRetail: r.inflowRetail != null ? new MoneyDataItem(r.inflowRetail) : null,
      inflowCost: r.inflowCost != null ? new MoneyDataItem(r.inflowCost) : null,
      markdowns: r.markdowns != null ? new MoneyDataItem(r.markdowns) : null,
      receivedRetailAcrossLocations: r.receivedRetailAcrossLocations != null ? new MoneyDataItem(r.receivedRetailAcrossLocations) : null,
      receivedCostAcrossLocations: r.receivedCostAcrossLocations != null ? new MoneyDataItem(r.receivedCostAcrossLocations) : null,
      // Derived
      salesPercentToGroup: null,
      salesPercentToTotal: null,
      inventoryPercentToGroup: null,
      inventoryPercentToTotal: null,
      inflowRetailPercentToGroup: null,
      inflowRetailPercentToTotal: null,
      cop: null,
      mmu: null,
      imu: null,
      markdownPercentage: null,
    }));
  };

  const fetchDataFn = async ({ clientId, startEpochMonth, endEpochMonth }: { clientId: string | number; startEpochMonth: number; endEpochMonth: number }) => {
    return await CustomReportService.getTootsiesClassPerformance({ clientId, startEpochMonth, endEpochMonth });
  };

  const extraControls = (
    <div className="flex flex-col items-start">
      <span className="text-xs font-semibold text-slate-600">Brands</span>
      <select
        value={showOnlyActuals ? 'actuals' : 'all'}
        onChange={e => setShowOnlyActuals(e.target.value === 'actuals')}
        className="h-10 px-4 border border-[#e1edf1] rounded-md text-[13px] font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-sky-200"
      >
        <option value="actuals">Only with actuals</option>
        <option value="all">All brands</option>
      </select>
    </div>
  );

  return (
    <div className="w-full h-full flex flex-col items-center justify-center text-center">
      <ClassPerformanceReport
        key={showOnlyActuals ? 'actuals' : 'all'}
        clientId={clientId}
        epochMonth={epochMonth}
        hierarchyOverride={brandHierarchy}
        fetchDataFn={fetchDataFn as any}
        parseDataFn={parseDataFn as any}
        extraControls={extraControls}
        defaultHierarchy={["locationCode", "category", "brand"]}
      />
    </div>
  );
};

export default TootsiesClassPerformanceReport;